import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import styles from './Search.module.css';

/**
 * Figma: `Search` (990:37054) — a bare search field: a 12x12 `search` glyph and
 * a single line of text in a 28px box. No label, no hint, no menu.
 *
 * **`Type` does not become a prop**, which the set's own annotation spells out:
 * "`Type` describes content/availability: `Placeholder` = empty, `Default` =
 * populated and idle, `Active` = focused, and `Disabled` = unavailable."
 *
 * So every one of them is derived — `value` being empty picks `Placeholder`, a
 * value picks `Default`, focus picks `Active`, and the `disabled` prop picks
 * `Disabled`. The only prop the axis contributes is `state`, which forces one
 * for documentation.
 *
 * **The field has no hover treatment.** It briefly had a second `State` axis
 * (`Default | Hover`) painting `component/search/surface-hover`; the designer
 * removed it on 2026-09-18, leaving the four `Type` variants. Nothing binds that
 * token now — the surface is the same in all four — so nothing here reacts to
 * the pointer. See plan.md §7 item 262.
 *
 * This is not `Select`'s `Type=Search`. That one is a combobox whose typing
 * filters a menu; this is a plain text field that reports its query and owns no
 * list. They share a shape and nothing else, and they now have separate token
 * sets in Figma (`component/search/*` against `component/select/*`).
 */

/**
 * Force a state for documentation and visual regression only.
 *
 * `active` is Figma's `Type=Active` — the focus ring — which is otherwise only
 * reachable by focusing the field, and so cannot be shown in a static grid.
 *
 * There is no `disabled` member: disabled is the `disabled` prop, which is real
 * input state rather than a rendering mode. There is no `hover` member either —
 * the design no longer draws one.
 */
export type SearchState = 'default' | 'active';

export type SearchProps = {
  /** Controlled query. Omit to let the field own it. */
  value?: string;
  defaultValue?: string;
  /** Fires on every keystroke. */
  onValueChange?: (value: string) => void;
  /**
   * Fires on Enter. Not in Figma, which defines no behaviour — but the Disabled
   * annotation says to "disable input *and submission* behavior", so the design
   * assumes a submit exists. Omit it and Enter does nothing.
   */
  onSearch?: (value: string) => void;
  /** Figma's `Placeholder` text layer. */
  placeholder?: string;
  /** Figma `Type=Disabled`. */
  disabled?: boolean;
  /**
   * Accessible name. The design draws no label, so without one the field is
   * announced only by its placeholder, which AT is not obliged to read. Falls
   * back to `placeholder` rather than leaving the control unnamed. Not in Figma.
   */
  'aria-label'?: string;
  /** Forwarded to the `<input>` so a caller can label or submit it by id. */
  id?: string;
  name?: string;
  /**
   * Focus the field on mount. Not in Figma, which has no focus order. Exists
   * for a dialog whose first focusable element is the wrong place to land — the
   * Tune sentiment modal's is the title's help icon, and focusing that opens its
   * tooltip over the corner of the table the moment the modal appears. A native
   * `<dialog>` honours the `autofocus` attribute on a descendant when it opens
   * — see the note on the input for why that has to be the attribute.
   */
  autoFocus?: boolean;
  state?: SearchState;
  className?: string;
};

export function Search({
  value,
  defaultValue = '',
  onValueChange,
  onSearch,
  placeholder = 'Search',
  disabled = false,
  'aria-label': ariaLabel,
  id,
  name,
  autoFocus,
  state,
  className,
}: SearchProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultValue);
  const current = value ?? uncontrolled;

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (value === undefined) setUncontrolled(next);
    onValueChange?.(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || onSearch === undefined) return;
    // Stops a field inside a <form> from submitting the form as well.
    event.preventDefault();
    onSearch(current);
  };

  /*
   * `interactive` is what makes `:focus-within` live. It is withheld while a
   * state is forced, so a states story stays inert, and while disabled — which
   * a disabled input gives for free, but stating it keeps the rule true if the
   * box ever holds a second focusable thing.
   */
  const classes = [
    styles.box,
    state === 'active' ? styles.active : null,
    disabled ? styles.disabled : null,
    state === undefined && !disabled ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    /*
     * A `<label>` rather than a `<div>`: it makes the whole 28px box focus the
     * field on click without a handler, which is what Figma's single `Active`
     * variant for the whole control implies. It wraps no text, so it adds no
     * accessible name of its own and `aria-label` still names the input.
     */
    <label className={classes}>
      {/* Decorative — the field is named by `aria-label`, not by the glyph. */}
      <Icon name="search" className={styles.icon} />
      <input
        className={styles.control}
        /* `search` so AT announces a searchbox; its native chrome is stripped in CSS. */
        type="search"
        id={id}
        name={name}
        /*
         * The real `autofocus` **attribute**, written by a ref — not React's
         * `autoFocus`, which never writes the attribute and just calls
         * `.focus()` on mount. Inside a `<dialog>`, `showModal()` runs its own
         * focusing steps afterwards and looks only for the attribute, so
         * React's version is overridden and focus lands on the dialog's first
         * focusable element instead. Refs attach before `Dialog`'s effect calls
         * `showModal()`, so the attribute is there when it looks.
         */
        ref={autoFocus ? (el) => el?.setAttribute('autofocus', '') : undefined}
        value={current}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel ?? placeholder}
        autoComplete="off"
        /* Not in Figma — a query is not prose, and iOS capitalising it is wrong. */
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
    </label>
  );
}
